import type { PaymentMethod, PaymentMethodType } from '../api/billingApi';

export function methodTypeLabel(methodType: PaymentMethodType): string {
  switch (methodType) {
    case 'CARD':
      return '카드';
    case 'BANK_TRANSFER':
      return '계좌이체';
    case 'VIRTUAL_ACCOUNT':
      return '가상계좌';
    default:
      return methodType;
  }
}

/** 별칭 > 카드사명(BE CardIssuerNames 매핑, 미등재 코드는 null) > 결제수단 종류 순으로 표기 —
 *  결제수단 관리·체크아웃 화면의 타일 제목, 체크아웃 요약 라벨이 공유한다. */
export function paymentMethodDisplayName(method: PaymentMethod): string {
  return method.cardAlias || method.cardIssuerName || methodTypeLabel(method.methodType);
}

/** 등록 직후 기본 별칭으로 저장할 값 — "카드사명 + 카드 뒷자리 4자리"(예: "우리카드 0052").
 *  cardIssuerName이 없으면(매핑 안 된 발급사 코드) methodTypeLabel로 대체한다. 등록 콜백
 *  (PaymentMethodPage.tsx·CheckoutPage.tsx의 registerCardMutation.onSuccess)이 공유한다
 *  (mobile payment_method_model.dart의 defaultAlias getter와 동일 규칙, 2026-09-21 —
 *  등록 시점엔 별칭이 null이라 카드사명으로만 표시되던 것을 바꿈). */
export function buildDefaultCardAlias(method: PaymentMethod): string {
  const prefix = method.cardIssuerName || methodTypeLabel(method.methodType);
  return method.cardLast4 ? `${prefix} ${method.cardLast4}` : prefix;
}

interface PaymentMethodCardBodyProps {
  method: PaymentMethod;
  /** 카드번호 마스킹 자릿수만 결정한다 — 결제수단 관리 화면(PaymentMethodPage)은 전체
   *  (•••• •••• •••• 1234), 좁은 카드 폭에 맞춰야 하는 타일(체크아웃 선택 타일 등)은 축약
   *  (•••• 1234) 표기. 은행명 줄은 이 값과 무관하게 항상 표시한다(공간을 거의 안 차지해서
   *  compact와 묶어 숨길 이유가 없음, 2026-09-16 — 구독관리 화면에서 필요해져 분리). 기본값
   *  false(전체 표기). */
  compact?: boolean;
}

/**
 * 결제수단 카드의 시각 정보 블록(카드사명·개인/법인·체크/신용 구분·마스킹 번호·은행명) — 결제수단
 * 관리 화면(PaymentMethodPage), 결제 확인 화면(CheckoutPage)의 카드 선택 타일, 구독 관리 화면
 * (SubscriptionManagePage)의 결제수단 요약이 공유한다(2026-09-16 컴포넌트화, 사용자 요청 —
 * 원래 체크아웃·구독관리 화면엔 카드사·개인법인 정보가 아예 없었음. 체크/신용 구분은 2026-09-17
 * 추가). 별칭 수정·기본 설정·삭제·선택 등 화면별 인터랙션은 이 컴포넌트 밖(제목 줄·액션 버튼)에서
 * 처리한다 — 여기선 순수 표시만 담당.
 */
export function PaymentMethodCardBody({ method, compact = false }: PaymentMethodCardBodyProps) {
  return (
    <>
      {method.cardIssuerName && (
        <p className="text-body-sm text-text-muted">
          {[method.cardOwnerType, method.cardType, method.cardIssuerName].filter(Boolean).join(' ')}
        </p>
      )}
      <p
        className={`font-bold text-text ${method.cardIssuerName ? 'mt-01' : ''} ${
          compact ? 'text-body-md' : 'text-title-sm'
        }`}
      >
        {method.methodType === 'CARD' && method.cardLast4
          ? compact
            ? `•••• ${method.cardLast4}`
            : `•••• •••• •••• ${method.cardLast4}`
          : method.methodType === 'BANK_TRANSFER' && method.bankAccountLast4
            ? `•••• ${method.bankAccountLast4}`
            : methodTypeLabel(method.methodType)}
      </p>
      {method.bankName && <p className="mt-01 text-body-sm text-text-muted">{method.bankName}</p>}
    </>
  );
}
